import { Form } from '@gofranz/formshive-common';
import {
  ActionIcon,
  Affix,
  Box,
  Button,
  CloseButton,
  Group,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Textarea,
  Title,
  Tooltip,
  TypographyStylesProvider,
} from '@mantine/core';
import { IconMessageCircle, IconSend } from '@tabler/icons-react';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRustyState } from '../../state';
import { FormDataRef } from './Edit';

const PANEL_WIDTH = 380;

export interface ChatAssistantProps {
  formId: string;
  formDataRef: React.RefObject<FormDataRef>;
  form: Form;
  opened: boolean;
  onToggle: () => void;
}

export function ChatAssistant({ formId, formDataRef, form, opened, onToggle }: ChatAssistantProps) {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [answer]);

  useEffect(() => {
    if (opened && !question && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [opened, question]);

  const handleSend = useCallback(async () => {
    if (!input.trim() || isStreaming) return;

    const currentInput = input.trim();
    setQuestion(currentInput);
    setInput('');
    setAnswer('');
    setError(null);
    setIsStreaming(true);

    const formState = {
      ...formDataRef.current,
      encryption_mode: form.encryption_mode,
      email_notification_type: form.email_notification_type,
    };

    const api = useRustyState.getState().api;

    await api.formChat(
      formId,
      currentInput,
      formState,
      (content: string) => {
        setAnswer(prev => prev + content);
      },
      () => {
        setIsStreaming(false);
      },
      (errorType: string) => {
        setIsStreaming(false);
        setError(errorType);
      },
    );
  }, [input, isStreaming, formId, formDataRef, form]);

  const handleNewQuestion = useCallback(() => {
    setQuestion('');
    setAnswer('');
    setError(null);
    setInput('');
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const answerHtml = useMemo(() => {
    if (!answer) return '';
    const raw = marked.parse(answer, { async: false }) as string;
    return DOMPurify.sanitize(raw);
  }, [answer]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  if (!opened) {
    return (
      <Affix position={{ bottom: 20, right: 20 }}>
        <Tooltip label={t('chat.openAssistant')} position="left">
          <ActionIcon
            size="xl"
            radius="xl"
            variant="filled"
            onClick={onToggle}
          >
            <IconMessageCircle size={24} />
          </ActionIcon>
        </Tooltip>
      </Affix>
    );
  }

  return (
    <Paper
      shadow="sm"
      withBorder
      style={{
        width: PANEL_WIDTH,
        minWidth: PANEL_WIDTH,
        height: 'calc(100vh - 80px)',
        position: 'sticky',
        top: 16,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Group justify="space-between" p="sm" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
        <Title order={5}>{t('chat.title')}</Title>
        <CloseButton onClick={onToggle} />
      </Group>

      <ScrollArea
        style={{ flex: 1 }}
        viewportRef={scrollRef}
        p="sm"
      >
        {question && (
          <Text size="sm" c="dimmed" mb="xs" fs="italic">
            {question}
          </Text>
        )}
        {answer && (
          <TypographyStylesProvider fz="sm">
            <div dangerouslySetInnerHTML={{ __html: answerHtml }} />
          </TypographyStylesProvider>
        )}
        {error && (
          <Text size="sm" c="red">
            {error === 'rateLimit' ? t('chat.rateLimit') : t('chat.error')}
          </Text>
        )}
        {isStreaming && !answer && (
          <Text size="sm" c="dimmed">...</Text>
        )}
        {!question && !error && (
          <Text size="sm" c="dimmed">
            {t('chat.placeholder')}
          </Text>
        )}
      </ScrollArea>

      <Box p="sm" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
        <Stack gap="xs">
          {(answer || error) && !isStreaming && (
            <Button
              variant="light"
              size="xs"
              fullWidth
              onClick={handleNewQuestion}
            >
              {t('chat.newQuestion')}
            </Button>
          )}
          {!question && (
            <Group gap="xs" align="flex-end">
              <Textarea
                ref={inputRef}
                placeholder={t('chat.placeholder')}
                value={input}
                onChange={e => setInput(e.currentTarget.value)}
                onKeyDown={handleKeyDown}
                autosize
                minRows={1}
                maxRows={4}
                style={{ flex: 1 }}
                disabled={isStreaming}
              />
              <ActionIcon
                size="lg"
                variant="filled"
                onClick={handleSend}
                disabled={!input.trim() || isStreaming}
              >
                <IconSend size={18} />
              </ActionIcon>
            </Group>
          )}
        </Stack>
      </Box>
    </Paper>
  );
}
